import { createApi, FetchArgs, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import Cookies from 'js-cookie';

import { TOKEN_COOKIE_NAME, WHITELISTED_PATHS } from '@/_lib/constants';
import { config } from '@/env';
import { getToken, isTokenExpired } from '@/helper/auth';
import { useAuthStore } from '@/store/useAuthStore';
import { trackApiError } from '@/lib/sentry';

const baseQuery = fetchBaseQuery({
  baseUrl: config.VITE_BACKEND_URL,
  prepareHeaders: headers => {
    const accessToken = Cookies.get(TOKEN_COOKIE_NAME);

    if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);
    headers.set('Accept-Language', 'eu-US');

    return headers;
  }
});

// Retry requests resulting in errors (skip 400, 401, 409 errors).
const baseQueryWithRetry = retry(
  async (args: string | FetchArgs, api, extraOptions) => {
    const url = typeof args === 'string' ? args : args.url;
    const urlPath = new URL(url, 'http://localhost').pathname; //dummy base used to make relative urls valid

    if (!WHITELISTED_PATHS.has(urlPath)) {
      const token = getToken();
      if (!token || isTokenExpired(token)) {
        useAuthStore.getState().triggerLogoutWarning();
        return { error: { status: 401, data: 'Token expired' } }; // needs to return something
      }
    }

    const result = await baseQuery(args, api, extraOptions);

    const status = result.error?.status;
    const isExpectedUserError = typeof status === 'number' && [400, 401, 403, 404].includes(status);

    if (result.error && !isExpectedUserError) {
      trackApiError(result.error, url, typeof args === 'string' ? 'GET' : args.method || 'GET');
    }

    // bail out of re-tries immediately for specific error codes,
    // because we know successive re-retries would be redundant
    if (result.error?.status && [400, 401, 409, 500].includes(result.error.status as number)) {
      retry.fail(result.error);
    }

    return result;
  },
  {
    maxRetries: 0
  }
);

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithRetry,
  tagTypes: ['RecommendedScenarios', 'UserBalance', 'Simulations'],
  endpoints: () => ({})
});
